import './MiwiInterface.scss';
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMiwiStore } from '../../../store';
import helpers from '../../../js/helpers';
import ExpandedSelectField from '../../expandedSelectField/ExpandedSelectField';
import ModalActionButtons from '../../modalActionButtons/ModalActionButtons';
import { type MiWiDevice } from '../../../types/miwi';
import DealershipList from '../../dealershipList/DealershipList';
import { DeviceType } from '@org-avp/device-models';

type MiwiInterfaceProps = {
    onClose: () => void;
};

const MiwiInterface = ({ onClose }: MiwiInterfaceProps) => {
    const { t } = useTranslation();
    const {
        overrideDevices: devices,
        overrideDeviceId,
        overrideDealerId,
        overrideMediaUrl,
        overrideLoading,
        overrideDeviceType,
        miwiLogout,
        connectDeviceChromebox,
        connectPrivateAve,
        connectNerdDevice,
        controlMedia2D,
        controlMedia3D,
        controlMedia,
        disconnect,
        fetchMiwiDevices,
        setOverrideDealerId
    } = useMiwiStore();
    const [overrideDevices, setOverrideDevices] = useState<MiWiDevice[]>([]);
    const [login, setLogin] = useState<boolean>(true);
    const [filterBy, setFilterBy] = useState<string>('All Devices');
    const [openConfirmConnect, setOpenConfirmConnect] = useState(false);
    const [openConfirmLogout, setOpenConfirmLogout] = useState(false);
    const [openConfirmDisconnect, setOpenConfirmDisconnect] = useState(false);

    useEffect(() => {
        setOverrideDevices(devices);
    }, [devices]);

    const getDevicesList = async (dealer) => {
        setOverrideDealerId(dealer.kvps);
        localStorage.setItem('overrideDealerId', dealer.kvps)
        await fetchMiwiDevices()
        setLogin(!login);
    };

    const setFilteredDevices = value => {
        if (value === DeviceType.CHROMEBOX) {
            const filteredOverrideDevices = devices.filter(
                device => device.avpType === DeviceType.CHROMEBOX
            );
            setOverrideDevices(filteredOverrideDevices);
            setFilterBy(DeviceType.CHROMEBOX);
        } else if (value === DeviceType.AVE) {
            const filteredOverrideDevices = devices.filter(device =>
                device.avpType === DeviceType.AVE
            );
            setOverrideDevices(filteredOverrideDevices);
            setFilterBy(DeviceType.AVE);
        } else if (value === 'NeRD') {
            const filteredOverrideDevices = devices.filter(device =>
                device.avpType === 'NeRD'
            );
            setOverrideDevices(filteredOverrideDevices);
            setFilterBy('NeRD');
        } else setOverrideDevices(devices);
        setFilterBy('All Devices');
    };

    const checkStatus = device => {
            return checkAvailability(device);
    };

    const checkAvailability = device => {
        if (device.available && overrideDeviceId === device.id && overrideMediaUrl) {
            return 'playing';
        }
        if (device.available && overrideDeviceId === device.id && !overrideMediaUrl) {
            return 'connected';
        }
        if (device.available) {
            return 'available';
        }
        return 'unavailable';
    };

    const renderDevices = () => {
        const list = overrideDevices.map(device => renderDevice(device));
        return list;
    };

    const renderDevice = device => {
        const itemClasses = classnames('devices-item', {
            unavailable: checkAvailability(device) === 'unavailable',
            connected: checkAvailability(device) === 'connected' || checkAvailability(device) === 'playing',
        });
         const pClasses = classnames('availability', {
            selected: checkAvailability(device) === 'connected',
            playing: checkAvailability(device) === 'playing'
        });
        const spanClasses = classnames('device', {
            selected: checkAvailability(device) === 'connected',
            playing: checkAvailability(device) === 'playing'
        });
        const item = (
            <div
                className={itemClasses}
                onClick={() => selectDevice(device)}
                key={device.id}
            >
                <span className={spanClasses}/>
                <p className="device-name">
                    {device.name !== '' ? device.name : device.hostname}
                    {/* <br />
                    {device.id} */}
                </p>
                <p className={pClasses}>{checkStatus(device)}</p>
                <p className="device-type">
                    {device.avpType}
                </p>
            </div>
        );
        return item;
    };

    const logout = () => {
        miwiLogout()
        onClose();
    };

    const openConfirmConnectHandle = () => {
        setOpenConfirmConnect(!openConfirmConnect);
    };

    const openConfirmDisconnectHandle = () => {
        setOpenConfirmDisconnect(!openConfirmDisconnect);
    };

    const openConfirmLogoutHandle = () => {
        setOpenConfirmLogout(!openConfirmLogout);
    };

    const onConfirmClick = async () => {
        if (overrideDeviceType === DeviceType.CHROMEBOX) {
            await controlMedia2D('SKIP')
        } else if (overrideDeviceType === DeviceType.AVE){
            await controlMedia3D('STOP')
        } else if (overrideDeviceType === 'NeRD'){
            await controlMedia('STOP')
        }        
        setFilteredDevices(filterBy);
        setOpenConfirmConnect(!openConfirmConnect);
    };

    // ToDo: change type to avpType to connect Nerd Device via naap api instead of miwi api
    const selectDevice = async device => {
        if (device.type === DeviceType.CHROMEBOX) {
            connectDeviceChromebox(device.id);
        } 
        if (device.type === DeviceType.AVE) {
            connectPrivateAve(device.id);
        }
        if (device.type === 'NeRD') {
            connectNerdDevice(device.id);
        }
        setFilteredDevices(filterBy);
    };

    const disconnectDevice = async () => {
        await disconnect();
        await fetchMiwiDevices();
        setFilteredDevices(filterBy);
        openConfirmDisconnectHandle();
    };

    const classes = classnames('b-miwi', {
        'is-loading': overrideLoading
    });
    if (
        !overrideDealerId &&
        !openConfirmConnect &&
        !openConfirmDisconnect &&
        !openConfirmLogout
    ) {
        return (
            <div className={classes}>
                <h3 className="header">{t('override_login')}</h3>
                <p className="header">{t('override_select_dealer')}</p>
                <DealershipList
                    onClick={getDevicesList}
                />
            </div>
        );
    }
    if (
        overrideDealerId &&
        !openConfirmConnect &&
        !openConfirmDisconnect &&
        !openConfirmLogout
    ) {
        const options = [
            { value: 'All Devices', label: t('all_devices') },
            { value: DeviceType.CHROMEBOX, label: DeviceType.CHROMEBOX },
            { value: DeviceType.AVE, label: DeviceType.AVE },
            { value: 'NeRD', label: 'NeRD'}

        ];
        return (
            <div className={classes}>
                <h3 className="header">{t('connect_with_device')}</h3>
                <p>{t('logged_in_as')}: {helpers.getOverrideDealerId()}</p>
                <div className="select">
                    <ExpandedSelectField
                        items={options}
                        selectedItem={filterBy}
                        onChangeHandler={setFilteredDevices}
                    />
                </div>
                <div className="devices-list">
                    {overrideDevices.length > 0 ? (
                        renderDevices()
                    ) : (
                        <p className="no-devices">{t('override_no_devices')}</p>
                    )}
                </div>
                <ModalActionButtons
                    localeCancel={`MiWi ${t('logout_action')}`}
                    localeAction={t('device_disconnect')}
                    onCancel={openConfirmLogoutHandle}
                    onAction={openConfirmDisconnectHandle}
                    disabled={!overrideDeviceId}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmConnect) {
        return (
            <div className={classes}>
                <h3 className="header">{t('connect_with_device')}</h3>
                <p className="header">{t('logged_in_as')}: {overrideDealerId}</p>
                <div className="message">
                    <p>{t('override_confirm_connect')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('override_connect')}
                    localeCancel={t('cancel')}
                    onAction={onConfirmClick}
                    onCancel={openConfirmConnectHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmDisconnect) {
        return (
            <div className={classes}>
                <h3 className="header">{t('device_disconnect')}</h3>
                <div className="message">
                    <p>{t('override_confirm_disconnect')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('override_disconnect')}
                    localeCancel={t('cancel')}
                    onAction={disconnectDevice}
                    onCancel={openConfirmDisconnectHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    if (openConfirmLogout) {
        return (
            <div className={classes}>
                <h3 className="header">{t('override_logout')}</h3>
                <div className="message">
                    <p>{t('override_confirm_logout')}</p>
                </div>
                <ModalActionButtons
                    localeAction={t('logout')}
                    localeCancel={t('cancel')}
                    onAction={logout}
                    onCancel={openConfirmLogoutHandle}
                    isLoading={overrideLoading}
                />
            </div>
        );
    }
    return '';
};

export default MiwiInterface;